/** Two readings of the same survey, drawn side by side.
 *
 * A `layout_mismatch` is the one issue a table cannot settle. The contributor
 * confirmed one layout; `signatures.json`, walking the compiled racing line
 * in driving order, identified another. Very often the two are the same
 * circuit run the other way round — Trial Mountain and Trial Mountain
 * Reverse trace an identical loop — so the names alone prove nothing and the
 * outlines alone prove nothing either. What settles it is the direction of
 * travel, which is why each panel carries an arrow on the line rather than
 * just the line.
 *
 * The geometry is whatever the merge job chose to send. When it sent points,
 * they are fitted to their own box and drawn; when it sent only the two
 * names, the design's stylised loop stands in and the arrows still disagree,
 * which is the fact the admin is being asked to rule on. Neither panel claims
 * to be the true one: the contributor's reading takes the accent because it
 * is the claim under review, the signature's takes the neutral ramp because
 * it is the evidence against it.
 */

import {
  boundsOfPoints,
  fitViewBox,
  pathFromPoints,
  viewBoxString,
  PLACEHOLDER_PATH,
  PLACEHOLDER_VIEWBOX,
} from '@/components/TrackOutline';
import type { LinePoint } from './issueDetail';

/** Where along the line the arrow sits. A third of the way round is clear of
 *  the start/finish straight on most circuits, so the mark is not lost in
 *  whatever else is drawn there. */
const ARROW_AT = 0.33;

interface Arrow {
  /** The shaft, from tail to tip. */
  shaft: { x1: number; y1: number; x2: number; y2: number };
  /** The head, as three points. */
  head: string;
  width: number;
}

/** A direction mark on the line, oriented by the local tangent. The tangent
 *  is measured over a window rather than between neighbours: consecutive
 *  samples of a racing line can be centimetres apart, and two of those decide
 *  nothing about which way the car was going. */
function arrowOn(points: readonly LinePoint[], span: number): Arrow | null {
  if (points.length < 2) return null;

  const window = Math.max(1, Math.floor(points.length / 24));
  const index = Math.min(points.length - 1 - window, Math.floor((points.length - 1) * ARROW_AT));
  const tail = points[Math.max(0, index)];
  const tip = points[Math.max(0, index) + window];
  if (!tail || !tip) return null;

  const dx = tip[0] - tail[0];
  const dy = tip[1] - tail[1];
  const length = Math.hypot(dx, dy);
  if (!Number.isFinite(length) || length === 0) return null;

  const ux = dx / length;
  const uy = dy / length;
  const shaftLength = span * 0.08;
  const head = span * 0.035;

  const x2 = tip[0];
  const y2 = tip[1];
  const x1 = x2 - ux * shaftLength;
  const y1 = y2 - uy * shaftLength;
  // Perpendicular, for the two back corners of the head.
  const px = -uy;
  const py = ux;

  return {
    shaft: { x1, y1, x2, y2 },
    head: [
      `${(x2 + ux * head).toFixed(2)},${(y2 + uy * head).toFixed(2)}`,
      `${(x2 - ux * head * 0.4 + px * head * 0.7).toFixed(2)},${(y2 - uy * head * 0.4 + py * head * 0.7).toFixed(2)}`,
      `${(x2 - ux * head * 0.4 - px * head * 0.7).toFixed(2)},${(y2 - uy * head * 0.4 - py * head * 0.7).toFixed(2)}`,
    ].join(' '),
    width: span * 0.012,
  };
}

/** The mock's own arrows, for the stylised loop: one reading walks the
 *  start/finish straight left to right, the other right to left. */
const PLACEHOLDER_ARROW = {
  forward: { shaft: { x1: 150, y1: 200, x2: 200, y2: 200 }, head: '200,194 212,200 200,206' },
  reverse: { shaft: { x1: 150, y1: 200, x2: 100, y2: 200 }, head: '100,194 88,200 100,206' },
} as const;

export interface ComparedLinePanelProps {
  /** "contributor's line", "signatures.json". */
  kicker: string;
  /** The layout this reading says the survey is of. */
  label: string;
  points: LinePoint[] | null;
  /** The claim under review takes the accent; the evidence takes neutral. */
  tone: 'accent' | 'neutral';
  /** Which way the stylised loop is walked when there is no geometry. */
  direction: 'forward' | 'reverse';
}

export function ComparedLinePanel({
  kicker,
  label,
  points,
  tone,
  direction,
}: ComparedLinePanelProps) {
  const stroke = tone === 'accent' ? 'var(--color-accent)' : 'var(--color-neutral-400)';
  const mark = tone === 'accent' ? 'var(--color-accent-200)' : 'var(--color-neutral-200)';

  const box = points ? boundsOfPoints(points) : null;
  const view = box ? fitViewBox(box, 0.1) : null;
  const drawn = points && view ? arrowOn(points, Math.max(view.width, view.height)) : null;
  const arrow = view ? drawn : PLACEHOLDER_ARROW[direction];
  const arrowWidth = drawn ? drawn.width : 5;

  return (
    <div
      style={{
        background: 'var(--color-bg)',
        borderRadius: 'var(--radius-md)',
        padding: 10,
        minWidth: 0,
      }}
    >
      <div
        style={{
          fontSize: 10,
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
          color: 'var(--color-neutral-500)',
          overflowWrap: 'anywhere',
        }}
      >
        {kicker} · {label}
      </div>
      <svg
        viewBox={view ? viewBoxString(view) : PLACEHOLDER_VIEWBOX}
        role="img"
        aria-label={`${kicker}: ${label}, with the direction of travel marked`}
        style={{ width: '100%', height: 180, display: 'block', marginTop: 6, overflow: 'visible' }}
      >
        <path
          d={points ? pathFromPoints(points) : PLACEHOLDER_PATH}
          fill="none"
          stroke={stroke}
          strokeWidth="2"
          strokeLinejoin="round"
          strokeLinecap="round"
          vectorEffect="non-scaling-stroke"
        />
        {arrow ? (
          <>
            <line
              x1={arrow.shaft.x1}
              y1={arrow.shaft.y1}
              x2={arrow.shaft.x2}
              y2={arrow.shaft.y2}
              stroke={mark}
              strokeWidth={arrowWidth}
              strokeLinecap="round"
            />
            <polygon points={arrow.head} fill={mark} />
          </>
        ) : null}
      </svg>
    </div>
  );
}

export interface LineCompareProps {
  contributor: { label: string; points: LinePoint[] | null };
  signature: { label: string; points: LinePoint[] | null };
}

export function LineCompare({ contributor, signature }: LineCompareProps) {
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      <ComparedLinePanel
        kicker="contributor's line"
        label={contributor.label}
        points={contributor.points}
        tone="accent"
        direction="forward"
      />
      <ComparedLinePanel
        kicker="signatures.json"
        label={signature.label}
        points={signature.points}
        tone="neutral"
        direction="reverse"
      />
    </div>
  );
}

export default LineCompare;
